/**
 * Duplication utility functions
 */

import { EditableElement, AnchorPoint, ShapeType } from '../editing/types';
import { createStickyNote, createOrganizer, createSection, createText, createShape } from '../editing/ElementFactory';

/**
 * Duplicate an element at a position based on anchor point
 * Uses default text for non-shape elements
 */
export function duplicateElementFromAnchor(
  sourceElement: EditableElement,
  sourceAnchor: AnchorPoint,
  offset: number = 50
): EditableElement {
  // Calculate position based on anchor side
  let newX: number;
  let newY: number;
  
  switch (sourceAnchor.side) {
    case 'right':
      newX = sourceAnchor.x + offset;
      newY = sourceAnchor.y - sourceElement.height / 2;
      break;
    case 'left':
      newX = sourceAnchor.x - offset - sourceElement.width;
      newY = sourceAnchor.y - sourceElement.height / 2;
      break;
    case 'bottom':
      newX = sourceAnchor.x - sourceElement.width / 2;
      newY = sourceAnchor.y + offset;
      break;
    case 'top':
      newX = sourceAnchor.x - sourceElement.width / 2;
      newY = sourceAnchor.y - offset - sourceElement.height;
      break;
    default:
      // Default to right side
      newX = sourceAnchor.x + offset;
      newY = sourceAnchor.y - sourceElement.height / 2;
  }

  // Create element with default text (don't copy source text)
  const options = {
    x: newX,
    y: newY,
    width: sourceElement.width,
    height: sourceElement.height,
    data: {},
  };
  
  // Copy style properties but not text content
  if (sourceElement.data) {
    const { text, title, ...styleData } = sourceElement.data;
    options.data = { ...styleData };
  }

  // Create new element based on source type with default text
  switch (sourceElement.type) {
    case 'sticky':
      return createStickyNote(options); // Default text: 'Double-click to edit'
    case 'text':
      return createText(options); // Default text: 'Double-click to edit'
    case 'organizer':
      return createOrganizer(options); // Default title: 'Section Title'
    case 'section':
      return createSection(options); // Default title: ''
    case 'shape':
      // For shapes, duplicate style/data (including text if present)
      const shapeType = sourceElement.data?.shapeType as ShapeType | undefined;
      if (shapeType) {
        return createShape(shapeType, { ...options, data: sourceElement.data });
      } else {
        // Fallback to rounded rectangle
        return createShape('rectangle-rounded', { ...options, data: sourceElement.data });
      }
    default:
      // Fallback to sticky note
      return createStickyNote(options);
  }
}
